import { getAssistance } from "./admin-assistance";
import type { ProviderFoodOrderEventPayload } from "./api";

export type RealtimeEventType =
  | "booking.created"
  | "booking.updated"
  | "session.started"
  | "session.updated"
  | "session.stopped"
  | "session.extension_requested.v1"
  | "food_order.created"
  | "food_order.updated"
  | "resync"
  | "queue.overflow";

interface RealtimeEventPayloadMap {
  "booking.created": unknown;
  "booking.updated": unknown;
  "session.started": unknown;
  "session.updated": unknown;
  "session.stopped": unknown;
  "session.extension_requested.v1": unknown;
  "food_order.created": ProviderFoodOrderEventPayload;
  "food_order.updated": ProviderFoodOrderEventPayload;
  resync: unknown;
  "queue.overflow": unknown;
}

export type RealtimeEvent = {
  [Type in RealtimeEventType]: {
    type: Type;
    payload: RealtimeEventPayloadMap[Type];
  };
}[RealtimeEventType];

let realtimeVersion = 0;
const versionListeners = new Set<() => void>();
const eventListeners = new Set<(event: RealtimeEvent) => void>();

function bumpRealtimeVersion() {
  realtimeVersion += 1;
  versionListeners.forEach((cb) => cb());
}

export function getRealtimeVersion() {
  return realtimeVersion;
}

export function subscribeRealtimeVersion(callback: () => void): () => void {
  versionListeners.add(callback);
  return () => {
    versionListeners.delete(callback);
  };
}

export function subscribeRealtimeEvent(callback: (event: RealtimeEvent) => void): () => void {
  eventListeners.add(callback);
  return () => eventListeners.delete(callback);
}

let eventSource: EventSource | null = null;
let connected = false;
const connectionListeners = new Set<(isConnected: boolean) => void>();

function setConnected(isConnected: boolean) {
  connected = isConnected;
  connectionListeners.forEach((cb) => cb(isConnected));
}

export function getIsRealtimeConnected() {
  return connected;
}

export function subscribeRealtimeConnection(
  callback: (isConnected: boolean) => void,
): () => void {
  connectionListeners.add(callback);
  return () => {
    connectionListeners.delete(callback);
  };
}

const EVENT_TYPES: RealtimeEventType[] = [
  "booking.created",
  "booking.updated",
  "session.started",
  "session.updated",
  "session.stopped",
  "session.extension_requested.v1",
  "food_order.created",
  "food_order.updated",
];

const RECOVERY_EVENT_TYPES: RealtimeEventType[] = ["resync", "queue.overflow"];

function parseEventPayload(data: string): unknown {
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return null;
  }
}

function emitRealtimeEvent(type: RealtimeEventType, data: string) {
  const event = { payload: parseEventPayload(data), type } as RealtimeEvent;
  eventListeners.forEach((callback) => callback(event));
  bumpRealtimeVersion();
}

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let isExplicitlyStopped = false;

function scheduleReconnect() {
  if (isExplicitlyStopped || reconnectTimer !== null) {
    return;
  }

  const delay = Math.min(
    RECONNECT_MAX_DELAY_MS,
    RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt,
  );
  reconnectAttempt += 1;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    startRealtimeEvents();
  }, delay);
}

function clearReconnectTimer() {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

export function startRealtimeEvents() {
  if (getAssistance()) return; // EventSource cannot carry the tab-specific assistance header.
  if (eventSource !== null) {
    return;
  }

  clearReconnectTimer();
  isExplicitlyStopped = false;

  const source = new EventSource("/api/v1/provider/events");
  eventSource = source;

  source.onopen = () => {
    if (eventSource !== source) return;
    setConnected(true);
    reconnectAttempt = 0;
  };

  source.onerror = () => {
    if (eventSource !== source) return;
    setConnected(false);
    source.close();
    eventSource = null;
    scheduleReconnect();
  };

  source.onmessage = () => {
    bumpRealtimeVersion();
  };

  for (const type of EVENT_TYPES) {
    source.addEventListener(type, (event) => {
      emitRealtimeEvent(type, (event as MessageEvent).data);
    });
  }

  // Optional protocol events tell consumers that the stream may have
  // dropped events and that authoritative data should be fetched.
  for (const type of RECOVERY_EVENT_TYPES) {
    source.addEventListener(type, (event) => {
      emitRealtimeEvent(type, (event as MessageEvent).data);
    });
  }
}

export function stopRealtimeEvents() {
  isExplicitlyStopped = true;
  clearReconnectTimer();
  if (eventSource !== null) {
    eventSource.close();
    eventSource = null;
  }
  setConnected(false);
  reconnectAttempt = 0;
}
